"use client";

import { AuthError, Session, User } from "@supabase/supabase-js";
import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "./supabase";
import { debugLog } from "./env";
import { adminQueryCache } from "./admin-query-cache";

interface AuthContextType {
	user: User | null;
	session: Session | null;
	loading: boolean;
	signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
	signOut: () => Promise<{ error: AuthError | null }>;
	refreshSession: () => Promise<boolean>;
	isAdmin: boolean;
	isManager: boolean;
	isEmployee: boolean;
	isCommunityManager: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [user, setUser] = useState<User | null>(null);
	const [session, setSession] = useState<Session | null>(null);
	const [loading, setLoading] = useState(true);

	// Check if user has admin, manager, employee, or community_manager role from user metadata
	const isAdmin = user?.user_metadata?.role === "admin";
	const isManager = user?.user_metadata?.role === "manager";
	const isCommunityManager = user?.user_metadata?.role === "community_manager";
	const isEmployee = user?.user_metadata?.role === "employee" || isAdmin || isManager;

	const signIn = async (email: string, password: string) => {
		setLoading(true);
		try {
			const { data, error } = await supabase.auth.signInWithPassword({
				email,
				password,
			});

			setLoading(false);
			return { error };
		} catch (error) {
			setLoading(false);
			return { error: error as AuthError };
		}
	};

	const signOut = async () => {
		setLoading(true);
		try {
			const { error } = await supabase.auth.signOut();
			setLoading(false);
			return { error };
		} catch (error) {
			setLoading(false);
			return { error: error as AuthError };
		}
	};

	const refreshSession = async () => {
		try {
			const { data, error } = await supabase.auth.refreshSession();
			if (error) {
				console.error("Error refreshing session:", error);
				return false;
			}
			return true;
		} catch (error) {
			console.error("Error refreshing session:", error);
			return false;
		}
	};

	useEffect(() => {
		// Get initial session
		const getInitialSession = async () => {
			const {
				data: { session },
				error,
			} = await supabase.auth.getSession();

			if (error) {
				console.error("Error getting session:", error);
				setLoading(false);
				return;
			}

			setSession(session);
			setUser(session?.user ?? null);
			setLoading(false);
		};

		getInitialSession();

		// Listen for auth changes
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((event, session) => {
			debugLog("Auth state changed:", event, session?.user?.email);

			setSession(session);
			if (event === "SIGNED_OUT") adminQueryCache.clear();
			setUser((current) => {
				const next = session?.user ?? null;
				if (
					current?.id !== next?.id ||
					current?.email !== next?.email ||
					JSON.stringify(current?.user_metadata) !== JSON.stringify(next?.user_metadata)
				) {
					adminQueryCache.clear();
					return next;
				}
				return current;
			});
			setLoading(false);
		});

		return () => {
			subscription.unsubscribe();
		};
	}, []);

	const value: AuthContextType = {
		user,
		session,
		loading,
		signIn,
		signOut,
		refreshSession,
		isAdmin,
		isManager,
		isEmployee,
		isCommunityManager,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook for admin-only access
export function useAdminAuth() {
	const auth = useAuth();

	if (!auth.isAdmin && !auth.isManager && !auth.isEmployee && !auth.isCommunityManager) {
		throw new Error("Admin, manager, employee, or community manager access required");
	}

	return auth;
}
